import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  ChevronLeft,
  Lock,
  FileText,
  Key,
  StickyNote,
  Users,
  Home as HomeIcon,
  Wallet,
  Plane,
  HeartPulse,
  Search,
  ShieldCheck,
} from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { DCard, HeroCard, Pill } from "@/components/dryvn";
import { CollapsibleList } from "@/components/collapsible-list";
// Screen migration (2026-07-05): vault metadata via hook (titles only;
// file opens stay 501-gated server-side until the GCS session).
import { type VaultItem } from "@/data/mock";
import { useVault } from "@/lib/queries";

export const Route = createFileRoute("/more/vault")({
  head: () => ({
    meta: [
      { title: "Vault — Dryvn Life" },
      {
        name: "description",
        content: "Titles only. Secret values are stored securely and never displayed on-screen.",
      },
    ],
  }),
  component: VaultPage,
});

const kindIcon = (k: VaultItem["kind"]) =>
  k === "password" ? Key : k === "document" ? FileText : StickyNote;
const catIcon = (c: VaultItem["category"]) =>
  c === "Home"
    ? HomeIcon
    : c === "Family"
      ? Users
      : c === "Money"
        ? Wallet
        : c === "Travel"
          ? Plane
          : HeartPulse;

const CATEGORIES: VaultItem["category"][] = ["Home", "Family", "Money", "Travel", "Health"];

function VaultPage() {
  const { data: vault } = useVault();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<VaultItem["category"] | "All">("All");

  const filtered = useMemo(
    () =>
      vault.filter(
        (v) =>
          (cat === "All" || v.category === cat) &&
          (q.trim() === "" || v.title.toLowerCase().includes(q.trim().toLowerCase())),
      ),
    [vault, q, cat],
  );

  const grouped = useMemo(() => {
    const map = new Map<VaultItem["category"], VaultItem[]>();
    for (const c of CATEGORIES) map.set(c, []);
    for (const it of filtered) map.get(it.category)?.push(it);
    return map;
  }, [filtered]);

  return (
    <AppShell>
      <Link
        to="/more"
        className="mb-4 inline-flex items-center gap-1 text-sm"
        style={{ color: "var(--dryvn-primary)" }}
      >
        <ChevronLeft className="h-4 w-4" /> More
      </Link>

      <HeroCard
        eyebrow="Private"
        title={
          <>
            <em className="italic" style={{ color: "var(--dryvn-primary-deep)" }}>
              Vault
            </em>
            .
          </>
        }
      >
        <div className="space-y-2">
          <p
            className="flex items-center gap-1.5 text-xs"
            style={{ color: "var(--dryvn-primary-deep)" }}
          >
            <Lock className="h-3 w-3" /> Titles only. Text Dryvn to retrieve a value — never shown
            here.
          </p>
          <div className="flex flex-wrap gap-2">
            <Pill tone="life">{vault.length} items</Pill>
            <Pill tone="primary">End-to-end encrypted</Pill>
          </div>
        </div>
      </HeroCard>

      {/* Search + filter */}
      <DCard className="mt-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div
            className="flex flex-1 items-center gap-2 rounded-full border px-4 py-2"
            style={{ borderColor: "var(--dryvn-line)" }}
          >
            <Search className="h-4 w-4" style={{ color: "var(--dryvn-grey)" }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search titles…"
              className="w-full bg-transparent text-sm outline-none"
              style={{ color: "var(--dryvn-ink)" }}
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(["All", ...CATEGORIES] as const).map((c) => {
              const active = cat === c;
              return (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className="rounded-full border px-3 py-1 text-xs transition-colors"
                  style={{
                    borderColor: active ? "var(--dryvn-primary)" : "var(--dryvn-line)",
                    backgroundColor: active
                      ? "color-mix(in oklab, var(--dryvn-life) 25%, transparent)"
                      : "transparent",
                    color: active ? "var(--dryvn-primary-deep)" : "var(--dryvn-grey)",
                  }}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>
      </DCard>

      {/* Grouped items */}
      <div className="mt-6 space-y-6">
        {CATEGORIES.map((c) => {
          const list = grouped.get(c) ?? [];
          if (list.length === 0) return null;
          const CatIcon = catIcon(c);
          return (
            <section key={c}>
              <div className="mb-2 flex items-center gap-2">
                <span
                  className="grid h-7 w-7 place-items-center rounded-lg"
                  style={{
                    backgroundColor: "color-mix(in oklab, var(--dryvn-life) 25%, transparent)",
                  }}
                >
                  <CatIcon className="h-3.5 w-3.5" style={{ color: "var(--dryvn-primary-deep)" }} />
                </span>
                <h3 className="font-display text-lg" style={{ color: "var(--dryvn-ink)" }}>
                  {c}
                </h3>
                <span className="font-mono text-[11px]" style={{ color: "var(--dryvn-grey)" }}>
                  {list.length}
                </span>
              </div>
              {/* The page's own search (title + category) is the filter this
                  collapse defers to — an active query renders in full. */}
              <CollapsibleList
                items={list}
                resource={`${c} items`}
                filtered={q.trim() !== "" || cat !== "All"}
              >
                {(shown) => (
                  <DCard className="p-0">
                    <ul>
                      {shown.map((v, i) => {
                        const KindIcon = kindIcon(v.kind);
                        return (
                          <li
                            key={v.id}
                            className="flex items-center gap-3 p-4"
                            style={{ borderTop: i === 0 ? "none" : "1px solid var(--dryvn-line)" }}
                          >
                            <span
                              className="grid h-9 w-9 shrink-0 place-items-center rounded-xl"
                              style={{ backgroundColor: "var(--dryvn-page-tint)" }}
                            >
                              <KindIcon
                                className="h-4 w-4"
                                style={{ color: "var(--dryvn-primary-deep)" }}
                              />
                            </span>
                            <div className="min-w-0 flex-1">
                              <p
                                className="truncate text-sm font-medium"
                                style={{ color: "var(--dryvn-ink)" }}
                              >
                                {v.title}
                              </p>
                              <p
                                className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[11px]"
                                style={{ color: "var(--dryvn-grey)" }}
                              >
                                <span>••••••••••</span>
                                <span>·</span>
                                <span>updated {v.updated}</span>
                                {v.sharedWith && (
                                  <span className="inline-flex items-center gap-1">
                                    <Users className="h-3 w-3" /> shared with {v.sharedWith}
                                  </span>
                                )}
                              </p>
                            </div>
                            <Pill tone="muted">{v.kind}</Pill>
                          </li>
                        );
                      })}
                    </ul>
                  </DCard>
                )}
              </CollapsibleList>
            </section>
          );
        })}
        {filtered.length === 0 && (
          <DCard>
            <p className="text-sm" style={{ color: "var(--dryvn-grey)" }}>
              No matches. Try a different search or category.
            </p>
          </DCard>
        )}
      </div>

      {/* SMS + trust footer */}
      <DCard className="mt-6">
        <p className="text-sm" style={{ color: "var(--dryvn-ink)" }}>
          Save something new by text:
        </p>
        <p
          className="mt-2 inline-block rounded-lg px-3 py-1.5 font-mono text-xs"
          style={{ backgroundColor: "var(--dryvn-page-tint)", color: "var(--dryvn-primary-deep)" }}
        >
          “vault: nexus card 123456 · category travel”
        </p>
        <p
          className="mt-3 flex items-center gap-1.5 text-[11px]"
          style={{ color: "var(--dryvn-grey)" }}
        >
          <ShieldCheck className="h-3 w-3" /> Values are encrypted at rest and never shown on this
          screen.
        </p>
      </DCard>
    </AppShell>
  );
}
